import { EnumProductAnalyticsEvent } from '@calm/evexia-analytics';
import { DS_HEIGHT, DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import {
    MediaPackProgram,
    MediaProgram,
    WorkflowWorkflowInfo,
} from '@calm/evexia-web-sdk';
import { ArrowForward, PlayArrowRounded } from '@mui/icons-material';
import Card, { CardProps } from '@rhg/components/card';
import { useAudio } from '@rhg/hooks/use-audio';
import { useGuidePlayer } from '@rhg/hooks/use-guide-player';
import { useHumanReadable } from '@rhg/hooks/use-human-readable';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { usePackContext } from '@rhg/hooks/use-pack-context';
import { ProgramContextProps } from '@rhg/hooks/use-program-context/types';
import { useProvidedProgramContext } from '@rhg/hooks/use-provided-program-context';
import { useTrackSessionEvent } from '@rhg/hooks/use-send-amplitude-event';
import commonMessages from '@rhg/lang/messages/common';
import { SxProps, Theme, useTheme } from '@rhg/libs/material';
import { CalmClinicalBadge } from '@rhg/libs/material/icons';
import { sxAppend } from '@rhg/libs/material/sx';
import { ProgramProvider } from '@rhg/providers/program';
import { ProgramState } from '@rhg/utils/programs';
import { routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { BannerCard } from '../banner-card';
import { CardVariant } from '../card/types';
import { ChipLabel } from '../chip-label';
import { ContributorAvatar } from '../contributor/avatar';
import { TimingState } from '../media/types';
import { BannerActions } from './banner-actions';
import { CompletedStatus } from './completed-status';

const focusIconStyle = {
    color: 'white',
    border: '2px solid white',
    borderRadius: '50%',
};

export const styleFocusIconHorizontal = {
    ...focusIconStyle,
    width: 60,
    height: 60,
    p: 3,
};

export const styleFocusIcon = {
    ...focusIconStyle,
    width: 80,
    height: 80,
    p: 4,
};
export const styleClinicalBadgeIcon = {
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: 'info.main',
    color: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'white' : 'black',
};

const styleImageIcon = (spacing: number) => ({ m: spacing });

const styleImageCompletedIcon = { width: 24, height: 24 };

export const styleCarouselContent = {
    minHeight: 152,
    justifyContent: 'flex-start',
};

const styleHorizontalCard = { width: { md: 442, sm: '100%' } };

export interface ProgramCardProps {
    variant: CardVariant;
    titleComponent?: React.ElementType;
    program: MediaProgram;
    packClass?: string;
    packProgram?: MediaPackProgram;
    workflow?: WorkflowWorkflowInfo;
    /** Hides the overlay icons (session, completed status, etc.) on the image. Doesn't hide the focus/hover overlay */
    hideImageOverlays?: boolean;
    isTopRecommendation?: boolean;
    displayGuideProgress?: boolean;
    imageForcedSize?: number | string;
    /** This prop affects only the carousel variant cards. It specifies whether or not
     * the category description is going to be displayed. It can be a boolean, which
     * ignores the program state (useful for DTC programs), or we can specify if we want
     * to display it based on the program state (useful for clinical programs), using a
     * program state to boolean mapping.
     */
    isCarouselCategoryDescriptionDisplayed?:
        | boolean
        | Record<
              | ProgramState.NotJoined
              | ProgramState.InProgress
              | ProgramState.Completed,
              boolean
          >;
    isContributorAvatarDisplayedOnCarousel?: boolean;
    carouselInProgressTitleMaxLines?: number;
    /** Provides information about the type/category of content (particularly useful in DTC content) */
    contentType?: string;
    additionalAction?: React.ReactNode;
    /** An action tied to the program workflow, either join or restart.
     * If this is defined, the enhanced banner actions are displayed, where
     * the first action triggers this function.
     */
    joinAction?: () => void;
    isNotClickable?: boolean;
    sx?: SxProps<Theme>;
    contentSx?: SxProps;
    CardProps?: Partial<CardProps>;
}
export function ProgramCard({
    program,
    workflow,
    packClass,
    packProgram,
    ...props
}: ProgramCardProps) {
    return (
        <ProgramProvider
            program={program}
            packClass={packClass}
            workflow={workflow}
            packProgram={packProgram}
        >
            <ContextAwareProgramCard {...props} />
        </ProgramProvider>
    );
}

type ContextAwareProgramCardProps = Omit<
    ProgramCardProps,
    keyof ProgramContextProps
>;

export const ContextAwareProgramCard = ({
    variant,
    titleComponent,
    isTopRecommendation = false,
    isCarouselCategoryDescriptionDisplayed,
    carouselInProgressTitleMaxLines = 1,
    isContributorAvatarDisplayedOnCarousel,
    contentType,
    additionalAction,
    hideImageOverlays,
    joinAction,
    sx,
    contentSx,
    imageForcedSize,
    isNotClickable,
    displayGuideProgress,
    CardProps = {},
}: ContextAwareProgramCardProps) => {
    const audioContext = useAudio();
    const {
        program,
        packClass,
        packProgram,
        progress: programProgress,
        currentSession,
        programType,
        programState,
        numSessions,
        workflow,
        toolsByGuide,
    } = useProvidedProgramContext();
    // TODO We should refactor to split this out of the plan top recommendation section
    const {
        palette: { mode },
    } = useTheme();
    const isDarkMode = mode === 'dark';
    const { openGuidePlayer } = useGuidePlayer();
    const { formatMessage } = useIntl();
    const { duration } = useHumanReadable();
    const { push } = useRouter();
    const isMobile = useIsMobile();
    const isBanner = variant === 'banner' || variant === 'bannerV2';
    const { trackPackItemEvent } = useTrackSessionEvent();
    const packTitle = usePackContext();

    const playHandler = useCallback(() => {
        if (currentSession && packClass && program) {
            openGuidePlayer({
                guide: currentSession,
                packClass,
                workflow,
                program,
                tools: toolsByGuide[currentSession.id],
                willSkipToPreviousProgress: true,
            });
        }
    }, [
        currentSession,
        openGuidePlayer,
        packClass,
        program,
        toolsByGuide,
        workflow,
    ]);

    const detailsRoute = useMemo(() => {
        if (packClass && program) {
            return routes.programDetails(packClass, program.id, 'true');
        }
        return undefined;
    }, [packClass, program]);

    const isClinicalProgram = useMemo(
        () => programType === 'clinical_program',
        [programType]
    );
    const inProgressDTC = useMemo(
        () =>
            !program?.isClinical &&
            !program?.completed &&
            program?.guides?.some((g) => g.completed),
        [program]
    );

    const inProgressClinical = useMemo(
        () => programState === ProgramState.InProgress,
        [programState]
    );

    const progressText = useCallback(
        (isChip: boolean) => {
            const numerator = currentSession?.position;
            const denominator = numSessions;
            return inProgressClinical || inProgressDTC
                ? isChip
                    ? formatMessage(commonMessages['common.of'], {
                          numerator,
                          denominator,
                      })
                    : formatMessage(commonMessages['common.session_of'], {
                          numerator,
                          denominator,
                      })
                : '';
        },
        [
            currentSession?.position,
            numSessions,
            inProgressClinical,
            inProgressDTC,
            formatMessage,
        ]
    );

    const progressTextAlt = useMemo(() => {
        if (
            displayGuideProgress &&
            currentSession?.totalDuration &&
            currentSession?.progressDuration
        ) {
            return formatMessage(commonMessages['common.percent_complete'], {
                percentage: currentSession?.progressDuration
                    ? Math.round(
                          (currentSession?.progressDuration /
                              currentSession?.totalDuration) *
                              100
                      )
                    : 0,
            });
        }
    }, [
        displayGuideProgress,
        currentSession?.totalDuration,
        currentSession?.progressDuration,
        formatMessage,
    ]);

    const progress = useCallback(
        ({
            isActiveAudio,
            timingState,
        }: {
            isActiveAudio: boolean;
            timingState?: TimingState;
        }) => {
            if (isActiveAudio && timingState) {
                return timingState.currentPercent;
            }

            // Don't show progress for the first session if it has no progress
            if (
                currentSession?.position === -1 ||
                (currentSession?.position === 1 &&
                    !currentSession.progressDuration)
            ) {
                return;
            }

            if (
                displayGuideProgress &&
                currentSession?.totalDuration &&
                (currentSession?.progressDuration ||
                    currentSession?.progressDuration === 0)
            ) {
                return (
                    currentSession?.progressDuration /
                    currentSession?.totalDuration
                );
            }
            return programProgress;
        },
        [
            programProgress,
            displayGuideProgress,
            currentSession?.totalDuration,
            currentSession?.progressDuration,
            currentSession?.position,
        ]
    );

    const sessionsText = useMemo(
        () =>
            formatMessage(commonMessages['common.sessions'], {
                sessions: numSessions,
            }),
        [numSessions, formatMessage]
    );

    const displayCategoryDescriptionOnCarousel = useMemo(() => {
        if (typeof isCarouselCategoryDescriptionDisplayed === 'boolean') {
            return isCarouselCategoryDescriptionDisplayed;
        }

        if (
            programState === ProgramState.Abandoned ||
            programState === ProgramState.Unspecified
        ) {
            return;
        }

        return isCarouselCategoryDescriptionDisplayed?.[programState];
    }, [isCarouselCategoryDescriptionDisplayed, programState]);

    const sxImageIcon = useMemo(
        () => styleImageIcon(variant === 'horizontal' ? 2 : 4),
        [variant]
    );

    const iconTop = useMemo(() => {
        if (hideImageOverlays) {
            return undefined;
        }

        switch (programState) {
            case ProgramState.NotJoined:
            case ProgramState.Abandoned:
                if (!isBanner && isTopRecommendation) {
                    return (
                        <ChipLabel
                            sx={sxImageIcon}
                            variant="light"
                            label={formatMessage(
                                commonMessages['common.top_recommendation']
                            )}
                        />
                    );
                }
                if (variant === 'bannerV2') {
                    return (
                        <ChipLabel
                            sx={sxImageIcon}
                            label={formatMessage(
                                commonMessages['common.sessions'],
                                {
                                    sessions: numSessions,
                                }
                            )}
                            variant="darkTransparent"
                        />
                    );
                }
                break;

            case ProgramState.InProgress || inProgressDTC:
                if (
                    variant === 'carousel' ||
                    variant === 'carouselSquare' ||
                    variant === 'bannerV2'
                ) {
                    if (displayCategoryDescriptionOnCarousel) {
                        return isTopRecommendation ? (
                            <ChipLabel
                                sx={sxImageIcon}
                                variant="light"
                                label={formatMessage(
                                    commonMessages['common.top_recommendation']
                                )}
                            />
                        ) : undefined;
                    }
                    return displayCategoryDescriptionOnCarousel ? undefined : (
                        <ChipLabel
                            sx={sxImageIcon}
                            label={progressText(true)}
                            variant="darkTransparent"
                        />
                    );
                }
                break;

            case ProgramState.Completed:
                if (variant === 'carousel' || variant === 'carouselSquare') {
                    if (displayCategoryDescriptionOnCarousel) {
                        return isTopRecommendation ? (
                            <ChipLabel
                                sx={sxImageIcon}
                                variant="light"
                                label={formatMessage(
                                    commonMessages['common.top_recommendation']
                                )}
                            />
                        ) : undefined;
                    }

                    return (
                        <ChipLabel
                            sx={sxImageIcon}
                            label={sessionsText}
                            variant="darkTransparent"
                        />
                    );
                }
                if (isBanner) {
                    return <CompletedStatus sx={sxImageIcon} size="large" />;
                }
                break;

            // non-in-progress DTC programs
            default:
                if (
                    (variant === 'horizontal' && isMobile) ||
                    ((variant === 'carousel' || variant === 'carouselSquare') &&
                        !displayCategoryDescriptionOnCarousel)
                ) {
                    return (
                        <ChipLabel
                            sx={sxImageIcon}
                            label={sessionsText}
                            variant="darkTransparent"
                        />
                    );
                }
                break;
        }
    }, [
        hideImageOverlays,
        programState,
        isBanner,
        isTopRecommendation,
        variant,
        inProgressDTC,
        isMobile,
        displayCategoryDescriptionOnCarousel,
        sxImageIcon,
        formatMessage,
        numSessions,
        progressText,
        sessionsText,
    ]);

    const categoryIcon = useMemo(
        () =>
            programType === 'clinical_program' ? (
                <CalmClinicalBadge sx={styleClinicalBadgeIcon} />
            ) : undefined,
        [programType]
    );

    const contentTag = useMemo(
        () =>
            isBanner && isTopRecommendation
                ? formatMessage(commonMessages['common.top_recommendation'])
                : undefined,
        [isBanner, isTopRecommendation, formatMessage]
    );

    const categoryTitleText = isClinicalProgram
        ? formatMessage(
              commonMessages['common.program_type.clinical_program.title']
          )
        : contentType;
    const categoryTitleColor = isClinicalProgram
        ? isDarkMode
            ? 'info.light'
            : undefined
        : 'text.sub';

    const categoryDescription = useMemo(() => {
        switch (programState) {
            case ProgramState.InProgress:
                if (variant === 'carousel' || variant === 'carouselSquare') {
                    return displayCategoryDescriptionOnCarousel
                        ? progressText(false)
                        : undefined;
                }
                return progressText(false);

            case ProgramState.Abandoned:
            case ProgramState.Completed:
            case ProgramState.NotJoined:
                if (variant === 'carousel' || variant === 'carouselSquare') {
                    return displayCategoryDescriptionOnCarousel
                        ? sessionsText
                        : undefined;
                }
                return sessionsText;

            // DTC programs, which have no program state
            default:
                if (
                    variant === 'carousel' ||
                    (variant === 'carouselSquare' &&
                        typeof isCarouselCategoryDescriptionDisplayed ===
                            'boolean')
                ) {
                    return isCarouselCategoryDescriptionDisplayed
                        ? sessionsText
                        : undefined;
                }

                if (isBanner || (variant === 'horizontal' && !isMobile)) {
                    return sessionsText;
                }

                return undefined;
        }
    }, [
        programState,
        variant,
        progressText,
        sessionsText,
        isCarouselCategoryDescriptionDisplayed,
        isBanner,
        isMobile,
        displayCategoryDescriptionOnCarousel,
    ]);

    const subtitle = useMemo(() => {
        if (inProgressClinical || inProgressDTC) {
            return currentSession?.title;
        }
        return program?.narrator?.name;
    }, [
        currentSession?.title,
        inProgressClinical,
        inProgressDTC,
        program?.narrator?.name,
    ]);

    const durationText = useMemo(
        () => duration(currentSession?.totalDuration),
        [currentSession?.totalDuration, duration]
    );

    const label = useMemo(() => {
        if (inProgressClinical || inProgressDTC) {
            return `${subtitle} ${durationText} ${formatMessage(
                commonMessages['common.play_session']
            )}`;
        } else {
            return `${program?.title} ${formatMessage(
                commonMessages['common.view_details']
            )}`;
        }
    }, [
        inProgressClinical,
        inProgressDTC,
        subtitle,
        durationText,
        formatMessage,
        program?.title,
    ]);

    const cardClickHandler = useCallback(() => {
        if (isNotClickable) {
            return undefined;
        }

        if (currentSession) {
            trackPackItemEvent({
                eventName: EnumProductAnalyticsEvent.PackItemClicked,
                guide: currentSession,
                program,
                packTitle,
            });
        }

        if (variant !== 'banner') {
            if (inProgressClinical || inProgressDTC) {
                return playHandler();
            }
            if (detailsRoute) {
                push(detailsRoute);
            }
        }
        return undefined;
    }, [
        isNotClickable,
        currentSession,
        variant,
        trackPackItemEvent,
        program,
        packTitle,
        inProgressClinical,
        inProgressDTC,
        detailsRoute,
        playHandler,
        push,
    ]);

    const titleIcon = useMemo(() => {
        if (
            programState !== ProgramState.InProgress &&
            (isBanner ||
                ((variant === 'carousel' || variant === 'carouselSquare') &&
                    isContributorAvatarDisplayedOnCarousel))
        ) {
            return <ContributorAvatar contributor={program?.narrator} />;
        }
        return undefined;
    }, [
        isBanner,
        isContributorAvatarDisplayedOnCarousel,
        program?.narrator,
        programState,
        variant,
    ]);

    const iconBottom = useMemo(() => {
        if (hideImageOverlays) {
            return undefined;
        }
        if (programState === ProgramState.Completed && !isBanner) {
            return (
                <CompletedStatus
                    sx={sxAppend(sxImageIcon, styleImageCompletedIcon)}
                    size="small"
                />
            );
        }

        return undefined;
    }, [hideImageOverlays, isBanner, programState, sxImageIcon]);

    const focusIcon = useMemo(() => {
        if (isNotClickable) {
            return undefined;
        }
        if (!isBanner) {
            if (inProgressClinical || inProgressDTC) {
                return (
                    <PlayArrowRounded
                        sx={
                            variant === 'horizontal'
                                ? styleFocusIconHorizontal
                                : styleFocusIcon
                        }
                    />
                );
            } else {
                return (
                    <ArrowForward
                        sx={
                            variant === 'horizontal'
                                ? styleFocusIconHorizontal
                                : styleFocusIcon
                        }
                    />
                );
            }
        }
        return undefined;
    }, [inProgressClinical, inProgressDTC, isBanner, isNotClickable, variant]);

    const titleMaxLines = useMemo(() => {
        if (variant === 'carousel' || variant === 'carouselSquare') {
            return inProgressClinical || inProgressDTC
                ? carouselInProgressTitleMaxLines
                : 2;
        }

        if (variant === 'horizontal') {
            return inProgressClinical || inProgressDTC ? 1 : 2;
        }

        return undefined;
    }, [
        carouselInProgressTitleMaxLines,
        inProgressClinical,
        inProgressDTC,
        variant,
    ]);

    const defaultContentSx = useMemo(
        () =>
            variant === 'carousel' || variant === 'carouselSquare'
                ? styleCarouselContent
                : undefined,
        [variant]
    );

    const isImageSideRadiusEnabled = useMemo(
        () => variant !== 'horizontal',
        [variant]
    );

    const imageSrc = useMemo(
        () => packProgram?.image?.url ?? program?.backgroundImage?.url ?? '',
        [packProgram?.image?.url, program?.backgroundImage?.url]
    );

    const isEntireCardClickable = !isNotClickable && !isBanner;

    if (variant === 'bannerV2') {
        let isActiveAudio = false;
        let isPlaying = false;
        let timingState = undefined;

        if ((inProgressClinical || inProgressDTC) && audioContext) {
            const {
                controls: { timingState: audioTimingState },
                mediaEl,
            } = audioContext;
            timingState = audioTimingState;
            const currentSessionUrl = currentSession?.assets[0].url;
            const audioElUrlRaw = mediaEl?.currentSrc as string;
            const audioElUrl = audioElUrlRaw ? audioElUrlRaw.split('?')[0] : '';
            isActiveAudio = currentSessionUrl === audioElUrl;
            isPlaying = !!(
                isActiveAudio &&
                !mediaEl?.paused &&
                (mediaEl?.duration ?? 0 > 0)
            );
        }

        return (
            <BannerCard
                title={subtitle ?? ''}
                titleMaxLines={titleMaxLines}
                label={label}
                titleComponent={titleComponent}
                subtitle={program?.title ?? ''}
                titleIcon={titleIcon}
                contentTag={contentTag}
                categoryTitle={categoryTitleText}
                categoryTitleColor={categoryTitleColor}
                categoryIcon={categoryIcon}
                categoryDescription={categoryDescription}
                progress={progress({ isActiveAudio, timingState })}
                progressText={progressText(false)}
                buttonGroup={
                    <BannerActions
                        joinAction={joinAction}
                        variant={variant}
                        playHandler={playHandler}
                        durationText={durationText}
                        detailsRoute={detailsRoute}
                        isPlaying={isPlaying}
                    />
                }
                buttonTopSpacing={
                    inProgressClinical || inProgressDTC
                        ? DS_SPACING_UNITS.CONTENT
                        : undefined
                }
                imageSrc={imageSrc}
                imageIconTop={iconTop}
                imageIconBottom={iconBottom}
                imageFocusIcon={focusIcon}
                imageForcedSize={
                    imageForcedSize
                        ? imageForcedSize
                        : DS_HEIGHT.HORIZONTAL_CARD_SIZE
                }
                onClick={cardClickHandler}
                contentSx={sxAppend(defaultContentSx, contentSx)}
                sx={sx}
                imageSideRadiusEnabled={isImageSideRadiusEnabled}
                isEntireCardClickable
                additionalAction={additionalAction}
                variant="bannerV2"
                entireCardButtonProps={{
                    'aria-label': program?.title ?? '',
                    'data-cy':
                        inProgressClinical || inProgressDTC
                            ? 'ProgramPlay'
                            : 'ProgramViewDetails',
                }}
                data-syntheticid="ProgramCard"
                {...CardProps}
            />
        );
    }
    return (
        <Card
            variant={variant}
            title={program?.title ?? ''}
            titleMaxLines={titleMaxLines}
            label={label}
            titleComponent={titleComponent}
            subtitle={subtitle ?? ''}
            titleIcon={titleIcon}
            contentTag={contentTag}
            categoryTitle={categoryTitleText}
            categoryTitleColor={categoryTitleColor}
            categoryIcon={categoryIcon}
            categoryDescription={categoryDescription}
            progress={progress({ isActiveAudio: false })}
            progressText={progressText(false)}
            progressTextAlt={progressTextAlt}
            buttonGroup={
                variant === 'banner' && (
                    <BannerActions
                        joinAction={joinAction}
                        variant={variant}
                        playHandler={playHandler}
                        durationText={durationText}
                        detailsRoute={detailsRoute}
                    />
                )
            }
            buttonTopSpacing={
                inProgressClinical || inProgressDTC ? 6 : undefined
            }
            imageSrc={imageSrc}
            imageAlt=""
            imageIconTop={iconTop}
            imageIconBottom={iconBottom}
            imageFocusIcon={focusIcon}
            imageForcedSize={
                imageForcedSize || variant !== 'horizontal'
                    ? imageForcedSize
                    : DS_HEIGHT.HORIZONTAL_CARD_SIZE
            }
            isNotClickable={isNotClickable}
            onClick={cardClickHandler}
            contentSx={sxAppend(defaultContentSx, contentSx)}
            sx={sxAppend(
                variant === 'horizontal' ? styleHorizontalCard : undefined,
                sx
            )}
            imageSideRadiusEnabled={isImageSideRadiusEnabled}
            isEntireCardClickable={isEntireCardClickable}
            additionalAction={additionalAction}
            entireCardButtonProps={{
                ...CardProps.entireCardButtonProps,
                'data-cy':
                    variant === 'banner'
                        ? undefined
                        : inProgressClinical || inProgressDTC
                        ? 'ProgramPlay'
                        : 'ProgramViewDetails',
            }}
            data-syntheticid="ProgramCard"
            {...CardProps}
        />
    );
};
